// useModuleEnabled — single source of truth for reading PER-PLAN feature
// modules (L3) in mobile components.
//
//   const on = useModuleEnabled(projectId, 'mod:vault');
//   if (!on) return <ModuleDisabledScreen moduleKey="mod:vault" ... />;
//
// WHICH FIELD THIS READS, AND WHY IT MATTERS
// ──────────────────────────────────────────
// It reads `plan_enabled` — layer 3 ALONE, the switch an administrator set for
// this plan. It deliberately does NOT read `enabled`, which is all four layers
// composed (platform flag AND account entitlement AND plan switch AND this
// member's permission).
//
// Gating navigation on the composed answer would SUBTRACT behaviour rather than
// add a layer:
//   - the platform flag is already enforced by useFeatureFlag/withFeatureGate,
//   - the account entitlement is already enforced by each feature's own opt-in
//     wall,
//   - a member's own permissions already govern what they can do inside.
// Hiding a screen because of those would remove surfaces that are visible in
// the app today. The backend draws exactly the same line: hand-placed gates
// call isModuleOnForPlan (L3 only) and only the catalog calls isModuleActive.
//
// DEFAULTS — this hook fails OPEN, on purpose
// ───────────────────────────────────────────
// An unknown plan, an unknown module key, or a catalog that has not arrived
// yet all read TRUE. Same rule as useFeatureFlag's defaultValue, and same rule
// as the backend's resolveStored(), which treats a NULL modules column as
// everything-on. The cost of failing open is a visible feature nobody turned
// on; the cost of failing closed is the app losing features because a request
// timed out. Only the second one looks like an outage.

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../store';
import { emptyPlanModuleState, type PlanModuleState } from '../store/reducers/planModules';
import { fetchPlanModules } from '../store/actions/planModules';

/** The whole catalog entry for one plan, or the empty state. */
export function usePlanModuleState(projectId?: string | number | null): PlanModuleState {
  const key = projectId == null ? '' : String(projectId);
  return useSelector(
    (state: RootState) =>
      (state as any).planModules?.byProject?.[key] || emptyPlanModuleState,
  );
}

/** Is this module switched on for this plan? Unknown reads as ON. */
export function useModuleEnabled(
  projectId: string | number | null | undefined,
  moduleKey: string,
): boolean {
  const slice = usePlanModuleState(projectId);
  if (!slice.fetched) return true;
  const entry = slice.modules.find((m) => m.key === moduleKey);
  // A key the server did not send is a key this client knows about and that
  // server does not. Treat it as on rather than hiding a feature because the
  // two are on different versions.
  if (!entry) return true;
  return entry.plan_enabled === true;
}

/**
 * A stable `isOn(moduleKey)` for callers that test several modules at once —
 * the navigation lists, which ask about every route they are about to render.
 *
 * Same semantics as useModuleEnabled: unfetched, unknown plan and unknown key
 * all read as ON.
 */
export function usePlanModuleIsOn(
  projectId?: string | number | null,
): (moduleKey: string) => boolean {
  const slice = usePlanModuleState(projectId);
  return React.useCallback(
    (moduleKey: string) => {
      if (!slice.fetched) return true;
      const entry = slice.modules.find((m) => m.key === moduleKey);
      if (!entry) return true;
      return entry.plan_enabled === true;
    },
    [slice.fetched, slice.modules],
  );
}

/**
 * Ensures the catalog for a plan has been fetched at least once.
 *
 * Deep links and notification taps land directly on a screen without passing
 * through anything that would have loaded the plan, so every guard calls this.
 * It is idempotent: one fetch per plan per session unless it fails.
 */
export function useEnsurePlanModules(projectId?: string | number | null): PlanModuleState {
  const dispatch = useDispatch();
  const slice = usePlanModuleState(projectId);
  const key = projectId == null ? '' : String(projectId);

  React.useEffect(() => {
    if (!key) return;
    // `attempted`, not `fetched`. A failed read deliberately leaves `fetched`
    // false so reads keep failing open — so keying the retry off `fetched`
    // re-dispatches forever once the failure clears `fetching`.
    if (slice.attempted || slice.fetching) return;
    dispatch(fetchPlanModules(key) as any);
  }, [dispatch, key, slice.attempted, slice.fetching]);

  return slice;
}

/**
 * HOC that renders <ModuleDisabledScreen /> in place of a screen whose module
 * is switched off for the plan in `route.params.projectId`.
 *
 * Use at the screen's default-export site, the same way withFeatureGate is
 * used — and compose the two when a screen has both a platform flag and a
 * module:
 *
 *   export default withFeatureGate(
 *     withModuleGate(VaultScreenInner, 'mod:vault', 'Vault'),
 *     'core_vault_enabled', 'Vault',
 *   );
 *
 * The wrapper holds the hooks and the inner component only mounts when the
 * module is on, which avoids the conditional-hook trap of inlining the check
 * inside an existing screen body.
 *
 * WHY A SCREEN GUARD EXISTS AT ALL when navigation lists are already filtered:
 * the lists are not the only way in. Deep links (linking.ts), notification taps
 * (RootNavigator), and cross-screen navigate() calls all reach a screen without
 * consulting any list. ProjectSmartReminders is the clearest case — six
 * notification navigations, a deep link and a home card, and no entry in
 * PROJECT_CATEGORIES at all, so list filtering alone would never touch it.
 */
export function withModuleGate<P extends object>(
  Component: React.ComponentType<P>,
  moduleKey: string,
  /**
   * Display name, only needed as a fallback. ModuleDisabledScreen prefers the
   * label the catalog already carries for this key, so the navigator does not
   * have to keep its own copy of eighteen feature names in sync with the
   * registry.
   */
  featureName?: string,
): React.ComponentType<P> {
  const Gated = (props: P) => {
    const projectId = (props as any)?.route?.params?.projectId;
    useEnsurePlanModules(projectId);
    const enabled = useModuleEnabled(projectId, moduleKey);

    // Deliberately NOT blanking the screen while the first read is in flight.
    // An earlier version returned null there to avoid a brief flash of a
    // feature that turns out to be off — but that trades a one-frame flash for
    // a BLANK SCREEN any time the catalog is slow, hanging, or unreachable,
    // which is the failure mode that actually hurts. Fail open here too: show
    // the screen, and swap it only once we positively know the module is off.

    if (!enabled) {
      return React.createElement(ModuleDisabledScreenLazy, { moduleKey, featureName, projectId });
    }
    return React.createElement(Component, props);
  };
  Gated.displayName = `WithModuleGate(${Component.displayName || Component.name || 'Component'})`;
  return Gated;
}

// Required lazily to keep this hook module importable from the store layer
// without dragging a component tree in behind it.
/* eslint-disable @typescript-eslint/no-require-imports */
const ModuleDisabledScreenLazy = (props: any) => {
  const Screen = require('../components/ModuleDisabledScreen').default;
  return React.createElement(Screen, props);
};
